import React from "react";

const ONBOARDING_TOUR_STEP_EVENT = "onboarding-tour-step-change";
const ONBOARDING_IMPORTED_TEMPLATE_EVENT = "onboarding-imported-template-change";

let currentTourStep: string | null = null;
let currentImportedTemplateId: string | null = null;

const syncBodyAttribute = (step: string | null) => {
  if (typeof document === "undefined") {
    return;
  }

  if (step) {
    document.body.setAttribute("data-onboarding-tour-step", step);
    return;
  }

  document.body.removeAttribute("data-onboarding-tour-step");
};

export const setOnboardingTourStep = (step: string | null) => {
  currentTourStep = step;
  syncBodyAttribute(step);

  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(ONBOARDING_TOUR_STEP_EVENT, {
      detail: { step },
    })
  );
};

export const getOnboardingTourStep = () => currentTourStep;

export const setOnboardingImportedTemplateId = (templateId: string | null) => {
  currentImportedTemplateId = templateId;

  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(ONBOARDING_IMPORTED_TEMPLATE_EVENT, {
      detail: { templateId },
    })
  );
};

export const getOnboardingImportedTemplateId = () => currentImportedTemplateId;

export const useOnboardingTourStep = () => {
  const [tourStep, setTourStep] = React.useState<string | null>(() => getOnboardingTourStep());

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const handleStepChange = (event: Event) => {
      const nextStep = (event as CustomEvent<{ step: string | null }>).detail?.step ?? null;
      setTourStep(nextStep);
    };

    window.addEventListener(ONBOARDING_TOUR_STEP_EVENT, handleStepChange);
    setTourStep(getOnboardingTourStep());

    return () => {
      window.removeEventListener(ONBOARDING_TOUR_STEP_EVENT, handleStepChange);
    };
  }, []);

  return tourStep;
};

export const useOnboardingImportedTemplateId = () => {
  const [templateId, setTemplateId] = React.useState<string | null>(() => getOnboardingImportedTemplateId());

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const handleTemplateChange = (event: Event) => {
      const nextTemplateId = (event as CustomEvent<{ templateId: string | null }>).detail?.templateId ?? null;
      setTemplateId(nextTemplateId);
    };

    window.addEventListener(ONBOARDING_IMPORTED_TEMPLATE_EVENT, handleTemplateChange);
    setTemplateId(getOnboardingImportedTemplateId());

    return () => {
      window.removeEventListener(ONBOARDING_IMPORTED_TEMPLATE_EVENT, handleTemplateChange);
    };
  }, []);

  return templateId;
};
